import { useTranslation } from 'react-i18next';

import { useLanguage } from './use-language.js';
import { Locale, SUPPORTED_LOCALES } from './locale.js';

const OUTER_CLASSES =
  'inline-flex items-center rounded-full border border-border bg-card p-0.5 shadow-sm';
const BUTTON_BASE =
  'min-h-touch min-w-touch rounded-full px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
const BUTTON_ACTIVE = 'bg-brand text-brand-foreground shadow-sm';
const BUTTON_IDLE = 'text-foreground/70 hover:text-foreground';

/**
 * Sprachumschalter DE/EN als segmented Pill: ein zusammenhängender
 * Outer-Container (Border + sanfter Schatten) mit zwei Inner-Buttons.
 * Aktive Sprache füllt sich in Brand-Farbe, inaktive bleibt textlich
 * gedämpft.
 *
 * Touch-Target: `min-h-touch`/`min-w-touch` an jedem Inner-Button sichert
 * 48 px Klickfläche, obwohl die Pills optisch kleiner wirken.
 *
 * `role="group"` plus `aria-label` macht das ARIA-Label für Tests stabil;
 * `aria-pressed` markiert die aktive Sprache.
 */
function localeKey(locale: Locale): 'language.de' | 'language.en' {
  return locale === Locale.De ? 'language.de' : 'language.en';
}

export function LanguageSwitcher(): JSX.Element {
  const { t } = useTranslation();
  const { locale, setLocale } = useLanguage();

  return (
    <div role="group" aria-label={t('language.switcherLabel')} className={OUTER_CLASSES}>
      {SUPPORTED_LOCALES.map((entry) => {
        const isActive = entry === locale;
        const className = `${BUTTON_BASE} ${isActive ? BUTTON_ACTIVE : BUTTON_IDLE}`;
        return (
          <button
            key={entry}
            type="button"
            aria-pressed={isActive}
            onClick={() => setLocale(entry)}
            className={className}
          >
            {t(localeKey(entry))}
          </button>
        );
      })}
    </div>
  );
}
